'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { CalendarIcon, Phone, X, Users, CalendarPlus2Icon as CalendarIcon2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import { useQueryState } from 'nuqs'
import { TypedLocale } from 'payload'

export default function BookingBar({ locale }: { locale: TypedLocale }) {
  const [isVisible, setIsVisible] = useState(false)
  const [isOpen, setIsOpen] = useState(true)
  const [isExpanded, setIsExpanded] = useState(false)
  const [checkIn, setCheckIn] = useQueryState('dateFrom')
  const [checkOut, setCheckOut] = useQueryState('dateTo')
  const [guests, setGuests] = useQueryState('guests', { defaultValue: '2' })
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  )
  const [openCheckIn, setOpenCheckIn] = useState(false)
  const [openCheckOut, setOpenCheckOut] = useState(false)

  const handleCheckInSelect = (date: Date | undefined) => {
    if (date) {
      const dateString = format(date, 'yyyy-MM-dd')
      setCheckIn(dateString || null)
    } else {
      setCheckIn(null)
    }
  }

  const handleCheckOutSelect = (date: Date | undefined) => {
    if (date) {
      const dateString = format(date, 'yyyy-MM-dd')
      setCheckOut(dateString || null)
    } else {
      setCheckOut(null)
    }
  }

  const handleGuestsChange = (newGuests: number) => {
    setGuests(newGuests.toString())
  }

  const checkInDate = checkIn ? new Date(checkIn) : undefined
  const checkOutDate = checkOut ? new Date(checkOut) : undefined
  const guestsNumber = parseInt(guests || '2', 10)

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 0.7) {
        setIsVisible(true)
      } else {
        setIsVisible(false)
      }
    }

    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const isMobile = windowWidth < 640

  return (
    <AnimatePresence>
      {isVisible && isOpen && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="pointer-events-none fixed bottom-4 left-0 right-0 z-50 mx-auto px-4 sm:bottom-8 sm:px-6"
          onMouseEnter={() => !isMobile && setIsExpanded(true)}
          onMouseLeave={() => !isMobile && setIsExpanded(false)}
        >
          <motion.div
            initial={{ width: 'auto' }}
            animate={{
              width: isExpanded ? '100%' : 'auto',
              maxWidth: isExpanded ? '42rem' : '24rem',
            }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
            }}
            className="mx-auto overflow-hidden border border-stone-200 bg-white/95 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-lg"
          >
            <div className="pointer-events-auto relative px-4 py-4 sm:px-6 sm:py-5">
              <AnimatePresence>
                {isExpanded && (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-4 top-4 cursor-pointer border-none bg-transparent text-gray-600 hover:text-gray-900"
                    onClick={() => setIsOpen(false)}
                  >
                    <X className="h-5 w-5" />
                  </motion.button>
                )}
              </AnimatePresence>

              <motion.div
                className="flex items-center justify-center gap-3 sm:gap-4"
                animate={{
                  flexDirection: isExpanded ? 'column' : 'row',
                  alignItems: 'center',
                }}
              >
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="w-full"
                  >
                    <h3 className="mb-4 text-center font-heading text-base text-gray-900 sm:mb-6 sm:text-lg">
                      {locale === 'de' ? 'Ihre' : 'Your'}
                      <span className="text-antonie-brown">Reservation</span>
                    </h3>

                    <div className="mb-4 flex w-full flex-col gap-2 sm:mb-6 sm:flex-row sm:gap-3">
                      <Popover open={openCheckIn} onOpenChange={setOpenCheckIn}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full sm:flex-1 justify-start text-left font-normal rounded-none border border-stone-200 hover:border-stone-400 focus:border-stone-500 focus:ring-stone-200 transition-all duration-300 text-sm sm:text-base bg-white text-gray-900 placeholder:text-stone-400',
                              !checkInDate && 'text-stone-400',
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-antonie-brown" />
                            {checkInDate ? (
                              format(checkInDate, 'PPP', { locale: de })
                            ) : (
                              <span>{locale === 'de' ? 'Anreise wählen' : 'Select arrival'}</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={checkInDate}
                            onSelect={handleCheckInSelect}
                            className="rounded-md border p-2"
                            locale={de}
                          />
                        </PopoverContent>
                      </Popover>

                      <Popover open={openCheckOut} onOpenChange={setOpenCheckOut}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full sm:flex-1 justify-start text-left font-normal rounded-none border border-stone-200 hover:border-stone-400 focus:border-stone-500 focus:ring-stone-200 transition-all duration-300 text-sm sm:text-base bg-white text-gray-900 placeholder:text-stone-400',
                              !checkOutDate && 'text-stone-400',
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-antonie-brown" />
                            {checkOutDate ? (
                              format(checkOutDate, 'PPP', { locale: de })
                            ) : (
                              <span>{locale === 'de' ? 'Abreise wählen' : 'Select departure'}</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={checkOutDate}
                            onSelect={handleCheckOutSelect}
                            defaultMonth={checkInDate ? new Date(checkInDate) : new Date()}
                            className="rounded-md border p-2"
                            locale={de}
                            disabled={(date) => date < (checkInDate || new Date())}
                          />
                        </PopoverContent>
                      </Popover>

                      <div className="flex w-min items-center rounded-none border border-stone-200 bg-white px-3 transition-all duration-300 hover:border-stone-400">
                        <Users className="mr-2 h-4 w-4 text-antonie-brown" />
                        <div className="flex w-full items-center justify-between gap-4">
                          <span className="text-xs text-gray-900 sm:text-sm">
                            {locale === 'de' ? 'Gäste' : 'Guests'}
                          </span>
                          <div className="flex items-center gap-3">
                            <button
                              className="cursor-pointer select-none p-2 text-stone-500 transition-colors hover:text-antonie-brown"
                              onClick={() => handleGuestsChange(Math.max(1, guestsNumber - 1))}
                              aria-label="Remove guest"
                            >
                              -
                            </button>
                            <span className="font-medium text-gray-900">{guestsNumber}</span>
                            <button
                              className="cursor-pointer select-none p-2 text-stone-500 transition-colors hover:text-antonie-brown"
                              onClick={() => handleGuestsChange(Math.min(10, guestsNumber + 1))}
                              aria-label="Add guest"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div className="flex justify-center gap-2 sm:gap-3">
                  <motion.div
                    animate={{
                      scale: isExpanded ? 1 : 0.95,
                      width: isExpanded ? 'auto' : 'auto',
                    }}
                    whileHover={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      href={{
                        pathname: locale === 'de' ? '/buchen' : '/book',
                        query: {
                          casaarrival: checkIn,
                          casadeparture: checkOut,
                          casaadults: guestsNumber,
                        },
                      }}
                    >
                      <Button
                        className={cn(
                          'rounded-none bg-antonie-red hover:bg-antonie-red-hover text-white transition-all duration-300 text-sm sm:text-base shadow-md font-semibold',
                          isExpanded ? 'px-6 sm:px-8' : 'px-4 sm:px-5',
                        )}
                        disabled={isExpanded && (!checkInDate || !checkOutDate)}
                      >
                        {!isExpanded && <CalendarIcon2 className="mr-2 h-4 w-4 text-white" />}
                        {locale === 'de' ? 'Jetzt Buchen' : 'Book now'}
                      </Button>
                    </Link>
                  </motion.div>

                  <motion.div
                    animate={{
                      scale: isExpanded ? 1 : 0.95,
                      width: isExpanded ? 'auto' : 'auto',
                    }}
                    whileHover={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      href={{
                        pathname: locale === 'de' ? '/kontakt' : '/contact',
                        query: {
                          dateFrom: checkIn,
                          dateTo: checkOut,
                          guests: guestsNumber,
                        },
                      }}
                    >
                      <Button
                        variant="outline"
                        className={cn(
                          'rounded-none border-antonie-green text-black hover:bg-antonie-green hover:text-black transition-all duration-300 text-sm sm:text-base font-semibold bg-white',
                          isExpanded ? 'px-6 sm:px-8' : 'px-4 sm:px-5',
                        )}
                      >
                        <Phone className="mr-2 h-4 w-4" />{' '}
                        {locale === 'de' ? 'Anfragen' : 'Inquiry'}
                      </Button>
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
